import React, { useState, useRef, useEffect } from 'react';
import UnifiedForm from './Components/UnifiedForm';
import { useTranslation } from 'react-i18next';
import './i18n';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { FaUsers, FaSignOutAlt, FaIdCard } from "react-icons/fa";
import JsBarcode from "jsbarcode";
import html2canvas from "html2canvas";
import { HiDownload } from "react-icons/hi"; // import download icon
import { FaMale, FaFemale, FaTransgenderAlt } from "react-icons/fa";
import PatientOpCard from "./Components/PatientOpCard";

function App() {
  const navigate = useNavigate();
  const opCardRef = useRef(null);
  const { t, i18n } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [warningCountdown, setWarningCountdown] = useState(60);
  const [otpVerified, setOtpVerified] = useState(
    localStorage.getItem("otpVerified") === "true"
  );
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("theme", isDark ? "dark" : "light");
  }, [isDark]);

  // Stay in sync when OTPVerification (login page) toggles the theme
  useEffect(() => {
    const handler = (e) => setIsDark(e.detail.isDark);
    window.addEventListener("themeChange", handler);
    return () => window.removeEventListener("themeChange", handler);
  }, []);
  const [isInsideApp, setIsInsideApp] = useState(false);
  const selectedPatient = JSON.parse(localStorage.getItem("patient") || "{}");
  const patientName = selectedPatient?.firstName || "Guest";
  const [showOpCard, setShowOpCard] = useState(false);

  const profilePic = selectedPatient?.profile_pic?.trim()
    ? selectedPatient.profile_pic
    : "/Images/default-avatar.png";

  const storedPatients = JSON.parse(localStorage.getItem("patients") || "[]");
  const hasMultiplePatients = storedPatients.length > 1;

  const dropdownRef = useRef(null);
  const barcodeRef = useRef(null);
  useEffect(() => {
    if (showOpCard && barcodeRef.current && selectedPatient.patientId) {
      JsBarcode(barcodeRef.current, selectedPatient.patientId, {
        format: "CODE128",
        width: 2,
        height: 50,
        displayValue: false,
        margin: 0
      });
    }
  }, [showOpCard, selectedPatient.patientId]);
  const logo = process.env.REACT_APP_HOSPITAL_LOGO;
  const defaultLang = process.env.REACT_APP_DEFAULT_LANG || 'en';
  const hospitalName = process.env.REACT_APP_PAY_HOSPITAL_NAME;
  const OpRow = ({ label, value }) => (
    <div className="flex justify-between">
      <span className="font-medium text-gray-600 dark:text-gray-400">{label}</span>
      <span className="font-semibold text-gray-900 dark:text-gray-100">
        {value || "-"}
      </span>
    </div>
  );
  const forceLogout = () => {
    localStorage.clear();
    setOtpVerified(false);
    navigate("/otp-verification", { replace: true });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const lsVal = localStorage.getItem("otpVerified") === "true";
      if (lsVal !== otpVerified) {
        setOtpVerified(lsVal);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [otpVerified]);

  useEffect(() => {
    const checkResume = () => {
      const last = localStorage.getItem("lastActiveAt");
      const timeout =
        parseInt(process.env.REACT_APP_INACTIVITY_TIMEOUT_IN_MS, 10) || 600000;

      if (!last) {
        localStorage.setItem("lastActiveAt", Date.now().toString());
        return;
      }

      if (Date.now() - Number(last) > timeout) {
        forceLogout();
      }
    };


    const handler = () => {
      if (document.visibilityState === "visible") checkResume();
    };

    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", checkResume);
  }, []);

  useEffect(() => {
    if (!otpVerified) return;

    let timer;
    let warnTimer;
    const timeout =
      parseInt(process.env.REACT_APP_INACTIVITY_TIMEOUT_IN_MS, 10) || 600000;
    const warnBefore = 60000; // warn 60s before timeout

    let lastReset = 0;
    const reset = () => {
      const now = Date.now();
      if (now - lastReset < 500) return; // throttle to once per 500ms
      lastReset = now;
      localStorage.setItem("lastActiveAt", now.toString());
      clearTimeout(timer);
      clearTimeout(warnTimer);
      setShowTimeoutWarning(false);
      setWarningCountdown(60);
      if (timeout > warnBefore) {
        warnTimer = setTimeout(() => {
          setShowTimeoutWarning(true);
          setWarningCountdown(60);
        }, timeout - warnBefore);
      }
      timer = setTimeout(forceLogout, timeout);
    };

    ["touchstart", "touchmove", "keydown", "mousemove", "mousedown"].forEach(e =>
      window.addEventListener(e, reset)
    );

    reset();

    return () => {
      clearTimeout(timer);
      clearTimeout(warnTimer);
      ["touchstart", "touchmove", "keydown", "mousemove", "mousedown"].forEach(e =>
        window.removeEventListener(e, reset)
      );
    };
  }, [otpVerified]);

  // Countdown tick while warning is visible
  useEffect(() => {
    if (!showTimeoutWarning) return;
    const id = setInterval(() => setWarningCountdown(c => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [showTimeoutWarning]);


  // Apply default language from env on app load
  useEffect(() => {
    i18n.changeLanguage(defaultLang);
    document.documentElement.setAttribute("lang", defaultLang);
  }, [defaultLang, i18n]);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    document.documentElement.setAttribute("lang", lang);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize OTP state from localStorage on mount
  useEffect(() => {
    setOtpVerified(localStorage.getItem('otpVerified') === 'true');
  }, []);

  // Optional: listen to localStorage changes (if OTP verified in another tab)
  useEffect(() => {
    const handleStorageChange = () => {
      setOtpVerified(localStorage.getItem("otpVerified") === "true");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);
  useEffect(() => {
    if (otpVerified) {
      localStorage.setItem("lastActiveAt", Date.now().toString());
    }
  }, [otpVerified]);
  const handleLogout = () => {
    localStorage.clear();
    setOtpVerified(false);
    setIsInsideApp(false);

    setDropdownOpen(false);
    window.location.reload();
  };

  return (

    <div className="min-h-screen px-2 py-4 font-sans transition-colors duration-500 relative">

      {/* Fixed animated gradient base */}
      <div className="app-gradient-bg" />

      {/* Fixed background layers */}
      <div className="app-dot-overlay" />
      <div className="app-blob app-blob-1" />
      <div className="app-blob app-blob-2" />
      <div className="app-blob app-blob-3" />
      <div className="app-med-icon" style={{ top: "8%", left: "3%", fontSize: "80px", animationDelay: "0s" }}>+</div>
      <div className="app-med-icon" style={{ bottom: "12%", right: "4%", fontSize: "100px", animationDelay: "-4s" }}>+</div>
      <div className="app-med-icon" style={{ top: "50%", left: "45%", fontSize: "60px", animationDelay: "-7s" }}>+</div>

      <div className="max-w-6xl mx-auto relative z-10">

        {/* ========== HEADER ========== */}
        {/* ========== HEADER ========== */}
        <div className="mb-3 sm:mb-4 overflow-visible relative z-40">
          <div className="rounded-2xl px-4 py-3 sm:px-6 sm:py-4 relative"
               style={{
                 background: isDark ? "rgba(15,23,42,0.58)" : "rgba(255,255,255,0.76)",
                 border: "1px solid " + (isDark ? "rgba(255,255,255,0.11)" : "rgba(255,255,255,0.7)"),
                 boxShadow: isDark
                   ? "0 32px 64px rgba(0,0,0,0.45), 0 8px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -1px 0 rgba(0,0,0,0.15)"
                   : "0 32px 72px rgba(15,40,120,0.22), 0 8px 24px rgba(15,40,120,0.12), inset 0 1px 0 rgba(255,255,255,1), inset 0 -1px 0 rgba(0,0,0,0.04)",
                 backdropFilter: "blur(28px)",
                 WebkitBackdropFilter: "blur(28px)",
               }}>

            {/* Row 1 — Hospital Name */}
            {otpVerified && (
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="app-logo-ring">
                  <img src={logo} alt="Logo" style={{ width: 40, height: 40, objectFit: "contain", borderRadius: "50%" }} />
                </div>
                <h1 className="text-lg sm:text-2xl md:text-3xl font-bold tracking-wide uppercase
               text-[#0f172a] dark:text-white truncate">
                  {hospitalName}
                </h1>
              </div>
            )}

            {/* Row 2 — single line, no wrap */}
            <div className="flex items-center justify-between gap-2 min-w-0">

              {/* Left — Logo + App Name (flex-1 so it shrinks and truncates) */}
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {!otpVerified && (
                  <img src="Images/TriloLogo_TRSP.png" alt="Logo" className="h-8 sm:h-10 w-auto flex-shrink-0" />
                )}

                <div className="min-w-0 truncate">
                  {!otpVerified && (
                    <p className="text-[11px] sm:text-sm font-semibold truncate text-[#1672B9] dark:text-[#6FB4FF]">
                      {t("login.companyName")}
                    </p>
                  )}

                  <p className={`font-extrabold tracking-wide truncate app-name-flow
    ${otpVerified ? "text-base sm:text-2xl" : "text-sm"}`}>
                    {t("login.appName")}
                  </p>
                </div>
              </div>

              {/* Right — Controls (flex-shrink-0 so it never wraps) */}
              <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">

                {/* Theme Toggle */}
                <button
                  onClick={() => setIsDark(d => !d)}
                  className="flex items-center justify-center rounded-xl transition-all active:scale-90 flex-shrink-0"
                  style={{
                    width: 30, height: 30,
                    background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)",
                    border: "1px solid " + (isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)"),
                    fontSize: 13,
                    touchAction: "manipulation",
                  }}
                  title={isDark ? "Switch to light mode" : "Switch to dark mode"}
                >
                  {isDark ? "☀️" : "🌙"}
                </button>

                {/* Language Pill Switcher */}
                <div className="flex rounded-xl p-0.5 gap-0.5 flex-shrink-0"
                     style={{ background: "var(--surface-hover)" }}>
                  {[
                    { code: "en", label: "EN" },
                    { code: "ml", label: "മല" },
                    { code: "kn", label: "ಕನ" },
                  ].map(({ code, label }) => (
                    <button
                      key={code}
                      onClick={() => changeLanguage(code)}
                      style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
                      className={`px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg text-[11px] sm:text-xs font-bold transition-all duration-200 min-h-[28px] sm:min-h-[34px] min-w-[28px] sm:min-w-[36px]
                        ${i18n.language === code
                          ? "bg-white dark:bg-gray-600 text-blue-700 dark:text-white shadow-sm"
                          : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white"
                        }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Profile */}
                {logo && otpVerified && (
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setDropdownOpen(p => !p)}
                      className="focus:outline-none ring-2 ring-transparent hover:ring-cyan-400 rounded-full transition"
                    >
                      <img
                        src={profilePic}
                        alt="Profile"
                        className="h-10 w-10 rounded-full object-cover shadow-md"
                        style={{ border: "2px solid rgba(59,130,246,0.3)" }}
                      />
                    </button>

                    {dropdownOpen && (
                      <div className="absolute right-0 mt-2 w-48 rounded-xl overflow-hidden z-50"
                           style={{
                             background: "var(--surface)",
                             border: "1px solid rgba(255,255,255,0.7)",
                             boxShadow: "0 16px 48px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
                             backdropFilter: "blur(20px)",
                             WebkitBackdropFilter: "blur(20px)",
                           }}>

                        <div className="px-4 py-2.5 flex flex-col gap-0.5"
                             style={{ borderBottom: "1px solid var(--border)" }}>
                          <div className="text-sm font-semibold flex items-center gap-1" style={{ color: "var(--text-primary)" }}>
                            <span className="truncate">Hi, {patientName}</span>
                            <span className="flex-shrink-0">👋</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                  style={{ background: "#10b981", animation: "dot-pulse 2s ease-in-out infinite" }} />
                            <span className="text-[10px] font-bold uppercase tracking-wider"
                                  style={{ color: "#059669" }}>Verified Patient</span>
                          </div>
                        </div>



                        {hasMultiplePatients && (
                          <button
                            onClick={() => {
                              const storedPatients = JSON.parse(localStorage.getItem("patients")) || [];
                              window.dispatchEvent(new CustomEvent("showPatientPopup", { detail: storedPatients }));
                              setDropdownOpen(false);
                            }}
                            className="group w-full px-4 py-2 text-left text-sm 
        text-gray-800 dark:text-gray-200
        flex items-center gap-2
        cursor-pointer
        hover:bg-blue-50 dark:hover:bg-blue-900/30
        transition-all duration-150"
                          >
                            <FaUsers className="text-blue-500 dark:text-blue-400 transition-transform group-hover:scale-110" />
                            <span className="group-hover:translate-x-0.5 transition-transform">
                              {t("common.switchPatient")}
                            </span>
                          </button>
                        )}

                        <button
                          onClick={() => {
                            setShowOpCard(true);
                            setDropdownOpen(false);
                          }}
                          className="group w-full px-4 py-2 text-left text-sm 
      text-gray-800 dark:text-gray-200
      flex items-center gap-2
      cursor-pointer
      hover:bg-teal-50 dark:hover:bg-teal-900/30
      transition-all duration-150"
                        >
                          <FaIdCard className="text-teal-500 dark:text-teal-400 transition-transform group-hover:scale-110" />
                          <span className="group-hover:translate-x-0.5 transition-transform">
                            {t("common.showOpCard")}
                          </span>
                        </button>

                        <button
                          onClick={handleLogout}
                          className="group w-full px-4 py-2 text-left text-sm 
      text-red-600 dark:text-red-400
      flex items-center gap-2
      cursor-pointer
      hover:bg-red-50 dark:hover:bg-red-900/30
      transition-all duration-150
      border-t border-gray-200 dark:border-gray-700"
                        >
                          <FaSignOutAlt className="text-red-500 dark:text-red-400 transition-transform group-hover:scale-110" />
                          <span className="group-hover:translate-x-0.5 transition-transform">
                            {t("common.logout")}
                          </span>
                        </button>

                      </div>

                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>

        <UnifiedForm
          onOtpVerified={() => {
            const now = Date.now().toString();
            localStorage.setItem("lastActiveAt", now);
            localStorage.setItem("otpVerified", "true");   // ← ADD THIS
            setOtpVerified(true);
            setIsInsideApp(true);
          }}
        />
        {/* ===== Static Footer Banner ===== */}
        {/* ===== Footer Banner Like Header ===== */}
        <div className="mt-4">
          <div className="px-4 py-3 sm:px-6 sm:py-4 rounded-2xl text-center text-xs font-semibold tracking-wide"
               style={{
                 background: isDark ? "rgba(15,23,42,0.58)" : "rgba(255,255,255,0.76)",
                 border: "1px solid " + (isDark ? "rgba(255,255,255,0.11)" : "rgba(255,255,255,0.7)"),
                 color: isDark ? "rgba(255,255,255,0.35)" : "rgba(15,40,120,0.45)",
                 boxShadow: isDark
                   ? "0 8px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)"
                   : "0 8px 24px rgba(15,40,120,0.1), inset 0 1px 0 rgba(255,255,255,1)",
                 backdropFilter: "blur(28px)",
                 WebkitBackdropFilter: "blur(28px)",
               }}>
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </div>
        </div>


      </div >
      <PatientOpCard
        show={showOpCard}
        onClose={() => setShowOpCard(false)}
        selectedPatient={selectedPatient}
        hospitalName={hospitalName}
        logo={logo}
      />



      {/* Session timeout warning modal */}
      {showTimeoutWarning && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div
            className="w-full max-w-xs rounded-3xl p-6 flex flex-col items-center gap-4"
            style={{
              background: isDark ? "#1e293b" : "#ffffff",
              border: "1px solid " + (isDark ? "#f97316" : "#fed7aa"),
              boxShadow: "0 32px 64px rgba(0,0,0,0.3)",
            }}
          >
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl"
              style={{ background: isDark ? "#431407" : "#fff7ed" }}
            >
              ⏱️
            </div>
            <div className="text-center">
              <p className="font-extrabold text-base mb-1"
                 style={{ color: isDark ? "#fb923c" : "#c2410c" }}>
                Session expiring soon
              </p>
              <p className="text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                You will be logged out in
              </p>
              <p
                className="text-5xl font-black mt-1"
                style={{ color: isDark ? "#fb923c" : "#ea580c", letterSpacing: "-0.03em" }}
              >
                {warningCountdown}s
              </p>
            </div>
            <p className="text-xs text-center" style={{ color: isDark ? "#64748b" : "#9ca3af" }}>
              Move or tap anywhere to stay logged in
            </p>
            <button
              onClick={() => {
                setShowTimeoutWarning(false);
                // Simulate activity to reset timers
                window.dispatchEvent(new MouseEvent("mousemove"));
              }}
              className="w-full py-3 rounded-xl font-bold text-white text-sm transition-all active:scale-95"
              style={{
                background: "linear-gradient(135deg,#2563eb,#0891b2)",
                boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
                touchAction: "manipulation",
                minHeight: 44,
              }}
            >
              Stay logged in
            </button>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
        toastClassName="rounded-md shadow-lg"
      />

    </div >
  );

}



export default App;
